'use client';

import { c as _c } from "react/compiler-runtime";
import 'maplibre-gl/dist/maplibre-gl.css';
import '~/assets/components/StadiaAttribution.css';
import { Search } from '@byway/icons';
import { Transition } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, type MapRef, Marker, NavigationControl, Source } from 'react-map-gl/maplibre';
import { generateCurveGeoJson, type MapOverviewItem } from '~/components/Playground/mapHelpers';
import type { GeoCoordinates } from '~/types/GeoLocation.types';
import { baseMapStyle } from '../map/mapStyles/outdoors';
import { MapOverviewMarker } from '../Playground/MapMarkers/MapMarkers';
import { MAP_CONSTANTS } from '../Playground/mapConstants';
interface Route {
  originGeoCoordinates: GeoCoordinates;
  destinationGeoCoordinates: GeoCoordinates;
}
interface StadiaMapProps {
  mapOverview: MapOverviewItem[];
  routes: Route[];
  maxZoom?: number;
  removeOriginFromDefaultBounds?: boolean;
  showLabelsByDefault?: boolean;
}
function StadiaMap(t0) {
  const $ = _c(55);
  const {
    mapOverview,
    maxZoom: t1,
    removeOriginFromDefaultBounds: t2
  } = t0;
  const maxZoom = t1 === undefined ? MAP_CONSTANTS.DEFAULT_MAX_ZOOM : t1;
  const removeOriginFromDefaultBounds = t2 === undefined ? MAP_CONSTANTS.MAP_CONFIG.REMOVE_ORIGIN_FROM_DEFAULT_BOUNDS : t2;
  const t = useTranslations("aria");
  const [map, setMap] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const mapRef = useRef(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [{
      lat: 90,
      lng: 90
    }, {
      lat: -90,
      lng: -90
    }];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const mapbounds = useRef(t3);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      setMap(e.target);
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const onLoad = t4;
  let t5;
  let t6;
  if ($[2] !== map || $[3] !== mapOverview || $[4] !== removeOriginFromDefaultBounds) {
    t5 = () => {
      if (!map) {
        return;
      }
      mapOverview.forEach((marker, index) => {
        if (removeOriginFromDefaultBounds && index === 0) {
          return;
        }
        const {
          latitude: lat,
          longitude: lng
        } = marker.geoCoordinates;
        if (lat < mapbounds.current[0].lat) {
          mapbounds.current[0].lat = lat;
        }
        if (lat > mapbounds.current[1].lat) {
          mapbounds.current[1].lat = lat;
        }
        if (lng < mapbounds.current[0].lng) {
          mapbounds.current[0].lng = lng;
        }
        if (lng > mapbounds.current[1].lng) {
          mapbounds.current[1].lng = lng;
        }
      });
      if (mapRef.current) {
        mapRef.current.fitBounds([[mapbounds.current[0].lng, mapbounds.current[0].lat], [mapbounds.current[1].lng, mapbounds.current[1].lat]], {
          padding: MAP_CONSTANTS.MAP_BOUNDS_PADDING
        });
      }
    };
    t6 = [map, mapOverview, removeOriginFromDefaultBounds];
    $[2] = map;
    $[3] = mapOverview;
    $[4] = removeOriginFromDefaultBounds;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] !== mapOverview) {
    t7 = generateCurveGeoJson(mapOverview);
    $[7] = mapOverview;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const curvedRoute = t7;
  let t8;
  if ($[9] !== mapOverview) {
    t8 = mapOverview.map(_temp);
    $[9] = mapOverview;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = {
      type: "FeatureCollection" as const,
      features: t8
    };
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const collisionPoints = t9;
  let t10;
  if ($[13] !== mapOverview[0] || $[14] !== mapOverview.length) {
    t10 = (marker_1, index_0) => {
      const isStart = index_0 === 0;
      const isEnd = index_0 === mapOverview.length - 1;
      if (isEnd && mapOverview.length > 1) {
        const startMarker = mapOverview[0];
        if (startMarker.geoCoordinates.latitude === marker_1.geoCoordinates.latitude && startMarker.geoCoordinates.longitude === marker_1.geoCoordinates.longitude) {
          return null;
        }
      }
      if (isStart) {
        return <MapOverviewMarker overviewItem={marker_1} variant="start" />;
      }
      if (isEnd) {
        return <MapOverviewMarker overviewItem={marker_1} variant="end" />;
      }
      return <MapOverviewMarker overviewItem={marker_1} variant="default" />;
    };
    $[13] = mapOverview[0];
    $[14] = mapOverview.length;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const renderMarkerContent = t10;
  const t11 = mapOverview[0]?.geoCoordinates?.latitude || 0;
  const t12 = mapOverview[0]?.geoCoordinates?.longitude || 0;
  let t13;
  if ($[16] !== t11 || $[17] !== t12) {
    t13 = {
      latitude: t11,
      longitude: t12,
      zoom: 6
    };
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      width: "100%",
      height: "100%"
    };
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== curvedRoute) {
    t15 = curvedRoute && <Source key="curved-route" id="curved-route" type="geojson" data={curvedRoute}><Layer key="route-background" {...MAP_CONSTANTS.LINE_STYLES.HALO} type="line" /><Layer key="route" {...MAP_CONSTANTS.LINE_STYLES.MAIN} /><Layer key="route-arrow" type="symbol" layout={MAP_CONSTANTS.ARROW_SYMBOL} paint={{
        "icon-color": "#000000"
      }} /></Source>;
    $[20] = curvedRoute;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Layer key="collision-circles" type="symbol" layout={MAP_CONSTANTS.COLLISION_CIRCLE} paint={{
      "icon-color": "#000000",
      "icon-opacity": 0
    }} />;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== collisionPoints) {
    t17 = <Source key="collision-points" id="collision-points" type="geojson" data={collisionPoints}>{t16}</Source>;
    $[23] = collisionPoints;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== mapOverview || $[26] !== renderMarkerContent) {
    let t19;
    if ($[28] !== mapOverview.length || $[29] !== renderMarkerContent) {
      t19 = (marker_2, index_1) => {
        const markerContent = renderMarkerContent(marker_2, index_1);
        if (!markerContent) {
          return null;
        }
        return <Marker key={`marker_${index_1}`} longitude={marker_2.geoCoordinates?.longitude} latitude={marker_2.geoCoordinates?.latitude} style={{
          zIndex: index_1 === 0 || index_1 === mapOverview.length - 1 ? MAP_CONSTANTS.MARKER_Z_INDEX.START_END : MAP_CONSTANTS.MARKER_Z_INDEX.BASE
        }}>{markerContent}</Marker>;
      };
      $[28] = mapOverview.length;
      $[29] = renderMarkerContent;
      $[30] = t19;
    } else {
      t19 = $[30];
    }
    t18 = mapOverview.map(t19);
    $[25] = mapOverview;
    $[26] = renderMarkerContent;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <NavigationControl showCompass={false} showZoom={true} position="bottom-right" />;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== maxZoom || $[33] !== t13 || $[34] !== t15 || $[35] !== t17 || $[36] !== t18) {
    t20 = <Map onLoad={onLoad} initialViewState={t13} maxZoom={maxZoom} ref={mapRef} style={t14} scrollZoom={false} mapStyle={baseMapStyle as any} reuseMaps={true} attributionControl={false}>{t15}{t17}{t18}{t19}</Map>;
    $[32] = maxZoom;
    $[33] = t13;
    $[34] = t15;
    $[35] = t17;
    $[36] = t18;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const MapComponent = t20;
  let t21;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = e_0 => {
      if (e_0.target === e_0.currentTarget) {
        setIsExpanded(false);
      }
    };
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== MapComponent) {
    t22 = <div className={MAP_CONSTANTS.EXPANDED_MAP_CLASSES.OVERLAY} onClick={t21}><div className={MAP_CONSTANTS.EXPANDED_MAP_CLASSES.CONTAINER}>{MapComponent}</div></div>;
    $[39] = MapComponent;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== isExpanded || $[42] !== t22) {
    t23 = <Transition as="div" show={isExpanded} enter={MAP_CONSTANTS.EXPANDED_MAP_TRANSITION.ENTER} enterFrom={MAP_CONSTANTS.EXPANDED_MAP_TRANSITION.ENTER_FROM} enterTo={MAP_CONSTANTS.EXPANDED_MAP_TRANSITION.ENTER_TO} leave={MAP_CONSTANTS.EXPANDED_MAP_TRANSITION.LEAVE} leaveFrom={MAP_CONSTANTS.EXPANDED_MAP_TRANSITION.LEAVE_FROM} leaveTo={MAP_CONSTANTS.EXPANDED_MAP_TRANSITION.LEAVE_TO}>{t22}</Transition>;
    $[41] = isExpanded;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setIsExpanded(true);
    };
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t) {
    t25 = t("map_expanded_view");
    $[45] = t;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t25) {
    t26 = <button type="button" className={MAP_CONSTANTS.EXPANDED_MAP_CLASSES.EXPAND_BUTTON} onClick={t24} data-ua-click="show-expanded-map-button"><Search ariaLabel={t25} color="var(--by-color-icon-contrast)" size="large" strokeWidth={2} /></button>;
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== MapComponent || $[50] !== t26) {
    t27 = <div className="relative w-full h-full">{MapComponent}{t26}</div>;
    $[49] = MapComponent;
    $[50] = t26;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t23 || $[53] !== t27) {
    t28 = <>{t23}{t27}</>;
    $[52] = t23;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  return t28;
}
function _temp(marker_0) {
  return {
    type: "Feature" as const,
    geometry: {
      type: "Point" as const,
      coordinates: [marker_0.geoCoordinates.longitude, marker_0.geoCoordinates.latitude]
    },
    properties: {}
  };
}
export { StadiaMap };